import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { errMsg, fmtDate, inr } from "@/lib/api";
import { useAdminApi } from "@/lib/admin";
import { useLoad, PageHead, DataTable, Td, NativeSelect } from "@/components/admin/kit";
import { StatusBadge } from "@/components/site/Notices";

const RefundForm = ({ p, kind, onDone }) => {
  const call = useAdminApi();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const submit = async () => {
    if (!window.confirm(`Refund ${amount ? "₹" + amount : "the full remaining amount"} for ${p.reference} via Razorpay?`)) return;
    try {
      await call("post", "/admin/refunds", { kind, ref_id: p.ref_id, amount: amount ? +amount : null, reason });
      toast.success("Refund initiated");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      <Input className="h-8 w-24" placeholder="₹ (full)" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid={`refund-amount-${p.id}`} />
      <Input className="h-8 w-40" placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`refund-reason-${p.id}`} />
      <Button size="sm" variant="destructive" className="h-8" disabled={reason.length < 3} onClick={submit} data-testid={`refund-submit-${p.id}`}>Refund</Button>
    </div>
  );
};

export default function Payments({ isAdmin }) {
  const call = useAdminApi();
  const [kind, setKind] = useState("application");
  const [f, setF] = useState({ q: "", status: "" });
  const [applied, setApplied] = useState(f);
  const [rows, reload] = useLoad(`/admin/payments/${kind}`, applied);
  const [refunds, reloadRefunds] = useLoad("/admin/refunds");
  const [refundFor, setRefundFor] = useState(null);

  const reconcile = async (p) => {
    try {
      const r = await call("post", `/admin/payments/${kind}/${p.id}/reconcile`);
      toast.success(`Razorpay status: ${r.status}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const syncAll = async () => {
    try {
      const r = await call("post", `/admin/payments/${kind}/sync-pending`);
      toast.success(`Checked ${r.checked} unpaid record(s) with Razorpay — ${r.now_paid} verified as paid`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHead title="Payments & refunds">{isAdmin && <Button variant="outline" onClick={syncAll} data-testid="payments-sync-pending">Sync pending with Razorpay</Button>}</PageHead>
      <Tabs value={kind} onValueChange={setKind} className="mb-4">
        <TabsList><TabsTrigger value="application" data-testid="payments-tab-application">Application fees</TabsTrigger><TabsTrigger value="order" data-testid="payments-tab-order">Product payments</TabsTrigger></TabsList>
      </Tabs>
      <div className="mb-4 flex flex-wrap gap-2">
        <Input className="h-9 w-72" placeholder="Transaction ID, order ID, application/order ref" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="payments-search-input" />
        <NativeSelect value={f.status} onChange={(v) => setF({ ...f, status: v })} placeholder="All" options={["paid", "created", "failed"]} testid="payments-filter-status" />
        <Button className="h-9" onClick={() => setApplied(f)} data-testid="payments-search-button">Search</Button>
      </div>
      <DataTable head={["Reference", "Razorpay order / payment", "Amount", "Status", "Receipt", "Date", "Refund", ""]} testid="payments-table">
        {rows?.map((p) => (
          <tr key={p.id} data-testid={`payment-row-${p.id}`}>
            <Td className="font-mono-num text-xs">{p.reference}</Td>
            <Td className="font-mono-num text-xs">{p.razorpay_order_id}<br />{p.razorpay_payment_id || "—"}</Td>
            <Td className="font-mono-num">{inr(p.amount)}</Td>
            <Td><StatusBadge status={p.status} />{p.error && <p className="mt-1 text-xs text-red-600">{p.error}</p>}</Td>
            <Td className="text-xs">{p.receipt_no || "—"}</Td><Td className="text-xs">{fmtDate(p.paid_at || p.created_at, true)}</Td>
            <Td className="text-xs">{p.refund_status ? `${p.refund_status} (${inr(p.refunded_amount)})` : "—"}{refundFor === p.id && <RefundForm p={p} kind={kind} onDone={() => { setRefundFor(null); reload(); reloadRefunds(); }} />}</Td>
            <Td>
              {isAdmin && p.status !== "paid" && <Button size="sm" variant="outline" onClick={() => reconcile(p)} data-testid={`payment-reconcile-${p.id}`}>Reconcile</Button>}
              {isAdmin && p.status === "paid" && p.refunded_amount < p.amount && <Button size="sm" variant="outline" onClick={() => setRefundFor(p.id)} data-testid={`payment-refund-${p.id}`}>Refund…</Button>}
            </Td>
          </tr>
        ))}
      </DataTable>
      <h2 className="mb-3 mt-10 font-display text-xl font-bold">Refund records</h2>
      <DataTable head={["Reference", "Type", "Amount", "Reason", "Status", "Razorpay refund ID", "By", "Date"]} testid="refunds-table">
        {refunds?.map((r) => (
          <tr key={r.id}><Td className="font-mono-num text-xs">{r.reference}</Td><Td>{r.kind}</Td><Td className="font-mono-num">{inr(r.amount)}</Td><Td>{r.reason}</Td><Td>{r.status}</Td><Td className="font-mono-num text-xs">{r.razorpay_refund_id}</Td><Td className="text-xs">{r.created_by}</Td><Td className="text-xs">{fmtDate(r.created_at, true)}</Td></tr>
        ))}
      </DataTable>
      <p className="mt-3 text-xs text-slate-500">Payments are marked paid only after Razorpay confirms them (checkout signature + server fetch, webhook, or Reconcile which queries Razorpay). There is no manual “mark as paid”.</p>
    </div>
  );
}
